import type { Account, ExternalProductAccount } from '@shared/products-types';
import { AccountType, MultibankingAccountType } from '@shared/products-types';
import type { Insurance } from '@retail/insurance-types';
import type { Pension } from '@retail/pension-types';
import { formatBankCodeAndNumber } from '@shared/formatting-account-number-utils';
import { formatIBAN } from '@shared/formatting-utils';
import { formatCreditCardNumber } from '@shared/cards-utils';
import type { ProductWithHelpers } from '@shared/products-core';
import { isCurrentAccount } from '@shared/products-core';
import type { SecurityResource } from '@retail/investments-types';
import { getFeatureConfig } from '@luna/feature-config';
import type { Leasing } from '@retail/leasing-types';

import type { BuildingSociety } from '../types';
import {
    isBuildingSociety,
    isCard,
    isInsurance,
    isLeasing,
    isLoanAccount,
    isPension,
    isSavingsAccount,
    isSecurityAccount,
} from './typeService';

const normalAccountTypes = [
    AccountType.CURRENT,
    AccountType.SAVING,
    AccountType.LOAN,
    MultibankingAccountType['EXT-BANK-CURRENT'],
];
/* these are the standard non-external, non-card accounts, with a .type field */
const isNormalAccountWithIban = (account: ProductWithHelpers<Account>): boolean =>
    normalAccountTypes.includes(account.type);

const isForeignAccount = (account: ProductWithHelpers): account is Account =>
    isCurrentAccount(account) && (account as Account).subtype === 'FOREIGN_ACCOUNT';

export const getProductNumber = (product: ProductWithHelpers): string => {
    const { preformattedAccountNumber } = getFeatureConfig((config) => config.loans.productConfig);
    const { preferredAccountNumberFormat } = getFeatureConfig(({ accounts }) => accounts);

    if (isLoanAccount(product) && preformattedAccountNumber) {
        return (product as ProductWithHelpers<Account>).accountno.number ?? '';
    }

    if (
        isSavingsAccount(product) &&
        product.flags?.includes('junior') &&
        (product as Account).subtype === 'SAVING_ACCOUNT_PIGGY'
    ) {
        return '';
    }

    if (preferredAccountNumberFormat === 'SPACEDBBAN' && isForeignAccount(product)) {
        return formatIBAN(product.accountno.iban);
    }

    if (product.formattedAccountNumber) {
        return product.formattedAccountNumber;
    }

    if (isNormalAccountWithIban(product as ProductWithHelpers<Account>)) {
        return getNormalProductNumber(product as ProductWithHelpers<Account>);
    }

    if (isCard(product)) {
        return getCardProductNumber(product as ProductWithHelpers<Account>);
    }

    if (isSecurityAccount(product)) {
        return getSecurityProductNumber(product);
    }

    if (isBuildingSociety(product)) {
        return getBuildingSocietyProductNumber(product);
    }

    if (isInsurance(product)) {
        return getInsuranceProductNumber(product);
    }

    if (isLeasing(product)) {
        return getLeasingProductNumber(product);
    }

    if (isPension(product)) {
        return getPensionProductNumber(product);
    }

    return '';
};

export function getNormalProductNumber(product: ProductWithHelpers<Account>): string {
    const { iban } = product.accountno;
    if (iban) {
        return formatIBAN(iban);
    }

    return formatBankCodeAndNumber(product.accountno.bankCode, product.accountno.number);
}

function getCardProductNumber(product: ProductWithHelpers<Account>): string {
    return formatCreditCardNumber(product.card?.number ?? product.underlyingCard?.number);
}

function getInsuranceProductNumber(
    product: ProductWithHelpers<ExternalProductAccount, Insurance>,
): string {
    return formatBankCodeAndNumber(null, product.underlyingData?.contractNumber);
}

export function getBuildingSocietyProductNumber(
    product: ProductWithHelpers<ExternalProductAccount, BuildingSociety>,
): string {
    const iban = product.underlyingData?.accountno?.iban;
    if (iban) {
        return formatIBAN(iban);
    }

    return formatBankCodeAndNumber(
        product.underlyingData?.accountno?.bankCode ?? null,
        product.underlyingData?.accountno?.number ?? product.underlyingData?.agreementNumber,
    );
}

function getPensionProductNumber(
    product: ProductWithHelpers<ExternalProductAccount, Pension>,
): string {
    return formatBankCodeAndNumber(null, product.underlyingData?.agreementNumber);
}

function getSecurityProductNumber(
    product: ProductWithHelpers<ExternalProductAccount, SecurityResource>,
): string {
    const accountno = product.underlyingData?.accountno;

    if (!accountno || typeof accountno === 'string') {
        return formatBankCodeAndNumber(null, accountno);
    }

    return formatBankCodeAndNumber(accountno.bankCode, accountno.number);
}

function getLeasingProductNumber(
    product: ProductWithHelpers<ExternalProductAccount, Leasing>,
): string {
    const iban = product.underlyingData?.accountno?.iban;
    if (iban) {
        return formatIBAN(iban);
    }

    return formatBankCodeAndNumber(
        product.underlyingData?.accountno?.bankcode,
        product.underlyingData?.accountno?.number,
    );
}
